
import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { translations } from '../translations';

type Language = 'he' | 'en';

interface LanguageContextType {
  language: Language;
  toggleLanguage: () => void;
  t: typeof translations.he;
  isRtl: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Initialize language based on browser preference
  const [language, setLanguage] = useState<Language>(() => {
    // Check if running in browser environment
    if (typeof window !== 'undefined' && window.navigator) {
      const browserLang = window.navigator.language || (window.navigator as any).userLanguage;
      // If language starts with 'en', default to English. Otherwise Hebrew.
      if (browserLang && browserLang.toLowerCase().startsWith('en')) {
        return 'en';
      }
    }
    return 'he';
  });

  useEffect(() => {
    document.documentElement.dir = language === 'he' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'he' ? 'en' : 'he');
  };

  const value = {
    language,
    toggleLanguage,
    t: translations[language],
    isRtl: language === 'he'
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
